import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useView } from '../context/ViewContext';
import {
  CalendarDaysIcon,
  MapPinIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  UserCircleIcon,
  HandRaisedIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  BellIcon,
  LockClosedIcon,
  ClockIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import FileDropzone from '../components/common/FileDropzone';
import { toast } from 'react-toastify';

/** Cohérent avec le filtrage backend (audience + visibility legacy) */
function getDocAudienceKey(doc) {
  if (doc.audience && ['all', 'team_with_volunteers', 'team_only'].includes(doc.audience)) {
    return doc.audience;
  }
  if (doc.visibility === 'public') return 'all';
  return doc.visibility === 'private' ? 'team_only' : 'all';
}

// Modal de confirmation générique avec option de notification
function ConfirmModal({
  title,
  message,
  confirmLabel = 'Confirmer',
  confirmClass = 'bg-red-600 hover:bg-red-700',
  withNotification = false,
  notificationLabel = 'Envoyer une notification',
  onConfirm,
  onCancel,
}) {
  const [sendNotif, setSendNotif] = useState(false);
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{message}</p>
        {withNotification && (
          <label className="flex items-center gap-2 mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={sendNotif}
              onChange={e => setSendNotif(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <div className="flex items-center gap-1 text-sm text-indigo-700">
              <BellIcon className="h-4 w-4" />
              {notificationLabel}
            </div>
          </label>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(sendNotif)}
            className={`px-4 py-2 text-white rounded-lg text-sm font-medium ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { activeView } = useView();
  const navigate = useNavigate();
  const location = useLocation();

  // Contexte détecté depuis l'URL : panel admin vs espace adhérent
  const isAdminContext = location.pathname.startsWith('/admin');
  const backUrl = isAdminContext ? '/admin/events' : '/events';

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setStatusLoading] = useState(false);
  const [volunteerLoading, setVolunteerLoading] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentForm, setDocumentForm] = useState({ file: null, note: '', accessLevel: 'all' });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [processingVolunteer, setProcessingVolunteer] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'admin' | 'pilot' | 'referent' | 'volunteer' | 'public'
  const [confirmModal, setConfirmModal] = useState(null);

  // Formulaire d'édition inline (référents sans accès à la page admin d'édition)
  const [editingEvent, setEditingEvent] = useState(false);
  const [eventForm, setEventForm] = useState({ title: '', description: '', status: '' });

  // Orientation de l'image de couverture ('portrait' | 'landscape' | null)
  const [, setCoverOrientation] = useState(null);

  // Onglet actif du panneau de gestion
  const [activeManagementTab, setActiveManagementTab] = useState('volontaires');

  // Onglets encart volontariat : participation | documents
  const [volunteerEncartTab, setVolunteerEncartTab] = useState('participation');

  // Mise à jour de l'audience d'un document
  const [updatingDocAudience, setUpdatingDocAudience] = useState(null);

  // Édition des notes de documents : { [docId]: string }
  const [docNoteEdits, setDocNoteEdits] = useState({});
  const [savingDocNote, setSavingDocNote] = useState(null);

  // Édition des tâches de volontaires : { [volunteerId]: { task, taskNote } }
  const [taskEdits, setTaskEdits] = useState({});
  const [savingTask, setSavingTask] = useState(null);
  const [removingVolunteer, setRemovingVolunteer] = useState(null);

  // Création de la communication depuis l'onglet Partager
  const [creatingCommunication, setCreatingCommunication] = useState(false);

  const [modalitesModalOpen, setModalitesModalOpen] = useState(false);
  const [modalitesDraft, setModalitesDraft] = useState('');
  const [savingModalites, setSavingModalites] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchEvent();
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setVolunteerEncartTab('participation');
  }, [id]);

  // Recalculer le rôle affiché (gestion vs volontariat) quand on bascule vue adhérent / admin
  useEffect(() => {
    if (event) determineUserRole(event);
  }, [activeView, event, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Si l’onglet « Modalités » n’est plus accessible (ex. refus de candidature), revenir sur « Participation »
  useEffect(() => {
    if (volunteerEncartTab === 'modalites' && event?.volunteerStatus?.status !== 'accepted') {
      setVolunteerEncartTab('participation');
    }
  }, [event?.volunteerStatus?.status, volunteerEncartTab, event]);

  // Détection de l'orientation de la cover image dès que l'URL est disponible
  useEffect(() => {
    const url = event?.coverImage?.url;
    if (!url) { setCoverOrientation(null); return; }
    const img = new window.Image();
    img.onload = () => {
      setCoverOrientation(img.naturalWidth > img.naturalHeight ? 'landscape' : 'portrait');
    };
    img.src = url;
  }, [event?.coverImage?.url]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvent = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/events/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvent(response.data);
      determineUserRole(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'événement:', error);
      if (error.response?.status === 403) {
        navigate(backUrl);
      } else {
        window.alert('Erreur lors du chargement de l\'événement');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const determineUserRole = (eventData) => {
    const userRoles = user.roles || [user.role];
    const inUserView = activeView === 'user';
    // En vue « adhérent », se comporter comme un adhérent pour l’affichage volontariat (pas l’espace de gestion)
    const viewVolunteerAsAdherent =
      inUserView && (userRoles.includes('admin') || userRoles.includes('super_admin'));

    if (eventData.referents?.some((ref) => String(ref._id) === String(user._id))) {
      setUserRole('referent');
      return;
    }

    if (!viewVolunteerAsAdherent) {
      if (userRoles.includes('super_admin')) {
        setUserRole('admin');
        return;
      }
      const userOrganismes = user.organismes || [];
      const concerned = eventData.organismesConcernes?.length
        ? eventData.organismesConcernes
        : (eventData.organismes || []);
      const organisers = eventData.organismesOrganisateurs?.length
        ? eventData.organismesOrganisateurs
        : concerned;
      const orgsPilot = [...new Set([...concerned, ...organisers])];
      if (
        userRoles.includes('admin') &&
        orgsPilot.some((org) => userOrganismes.includes(org))
      ) {
        setUserRole('pilot');
        return;
      }
    }

    if (eventData.volunteerStatus) {
      setUserRole('volunteer');
      return;
    }
    setUserRole('public');
  };

  const saveModalitesOrganisation = async () => {
    try {
      setSavingModalites(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/events/${id}`,
        { modalitesOrganisation: modalitesDraft },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchEvent({ silent: true });
      setModalitesModalOpen(false);
      toast.success('Modalités enregistrées');
    } catch (e) {
      toast.error(e.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSavingModalites(false);
    }
  };

  // ── Communication de partage ────────────────────────────────────────────────
  const handleCreateCommunication = async () => {
    try {
      setCreatingCommunication(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/events/${id}/create-communication`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/admin/communications/edit/${response.data.communicationId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la création de la communication');
    } finally {
      setCreatingCommunication(false);
    }
  };

  // ── Volontariat (espace adhérent) ───────────────────────────────────────────
  const handleVolunteerAction = async (action) => {
    try {
      setVolunteerLoading(true);
      const token = localStorage.getItem('token');
      if (action === 'register') {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/events/${id}/volunteer`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data?.message) toast.success(data.message);
      } else {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/events/${id}/volunteer`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Participation annulée.');
      }
      await fetchEvent({ silent: true });
    } catch (error) {
      window.alert(error.response?.data?.message || 'Erreur lors de l\'action');
    } finally {
      setVolunteerLoading(false);
    }
  };

  const downloadEventDocument = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/events/${id}/documents/${docId}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.downloadUrl, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Téléchargement impossible');
    }
  };

  // ── Documents ───────────────────────────────────────────────────────────────
  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!documentForm.file) { window.alert('Veuillez sélectionner un fichier'); return; }
    try {
      setUploadingDocument(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', documentForm.file);
      formData.append('audience', documentForm.accessLevel);
      if (documentForm.note) formData.append('note', documentForm.note);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/events/${id}/documents`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setDocumentForm({ file: null, note: '', accessLevel: 'all' });
      setShowDocumentModal(false);
      await fetchEvent({ silent: true });
    } catch (error) {
      window.alert(error.response?.data?.message || 'Erreur lors de l\'upload du document');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = (documentId) => {
    setConfirmModal({ type: 'deleteDoc', payload: { documentId } });
  };

  const AUDIENCE_LABELS = {
    all: 'Tous',
    team_only: 'Équipe seule',
    team_with_volunteers: 'Équipe + volontaires',
  };

  const handleDocumentAudienceChange = async (documentId, newAudience) => {
    try {
      setUpdatingDocAudience(documentId);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/events/${id}/documents/${documentId}`,
        { audience: newAudience },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Visibilité mise à jour : ${AUDIENCE_LABELS[newAudience] ?? newAudience}`);
      await fetchEvent({ silent: true });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la visibilité:', error);
      toast.error('Impossible de modifier la visibilité du document.');
    } finally {
      setUpdatingDocAudience(null);
    }
  };

  const saveDocumentNote = async (documentId) => {
    const note = docNoteEdits[documentId];
    if (note === undefined) return;
    try {
      setSavingDocNote(documentId);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/events/${id}/documents/${documentId}`,
        { note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note mise à jour');
      setDocNoteEdits(prev => { const n = { ...prev }; delete n[documentId]; return n; });
      await fetchEvent({ silent: true });
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la note:', error);
      toast.error('Impossible de modifier la note.');
    } finally {
      setSavingDocNote(null);
    }
  };

  const executeDeleteDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/events/${id}/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchEvent({ silent: true });
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
    }
  };

  // ── Statut ──────────────────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus, sendNotification = false) => {
    try {
      setStatusLoading(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/events/${id}/status`,
        { status: newStatus, sendNotification },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchEvent({ silent: true });
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  // ── Édition inline (référents sans accès au formulaire admin) ──────────────
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/events/${id}`,
        { title: eventForm.title, description: eventForm.description, status: eventForm.status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingEvent(false);
      await fetchEvent({ silent: true });
    } catch (error) {
      window.alert(error.response?.data?.message || 'Erreur lors de la modification');
    }
  };

  // ── Volontaires (gestion par référent/admin) ────────────────────────────────
  const handleVolunteerDecision = (volunteerId, action) => {
    setConfirmModal({ type: 'volunteerDecision', payload: { volunteerId, action } });
  };

  const handleRemoveVolunteerRequest = (volunteer) => {
    const name = [volunteer.userId?.prenom, volunteer.userId?.nom].filter(Boolean).join(' ').trim();
    setConfirmModal({
      type: 'removeVolunteer',
      payload: {
        volunteerId: volunteer._id,
        name: name || 'ce volontaire',
      },
    });
  };

  const executeVolunteerDecision = async (volunteerId, action, sendNotification = false) => {
    try {
      setProcessingVolunteer(volunteerId);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/events/${id}/volunteers/${volunteerId}`,
        { status: action === 'accepte' ? 'accepted' : 'rejected', sendNotification },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchEvent({ silent: true });
    } catch (error) {
      console.error('Erreur lors du traitement:', error);
    } finally {
      setProcessingVolunteer(null);
    }
  };

  // ── Tâche volontaire ────────────────────────────────────────────────────────
  const initTaskEdit = (volunteer) => {
    setTaskEdits(prev => ({
      ...prev,
      [volunteer._id]: {
        task: volunteer.task || '',
        taskNote: volunteer.taskNote || '',
      }
    }));
  };

  const saveVolunteerTask = async (volunteerId) => {
    const edit = taskEdits[volunteerId];
    if (!edit) return;
    try {
      setSavingTask(volunteerId);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/events/${id}/volunteers/${volunteerId}/task`,
        { task: edit.task, taskNote: edit.taskNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchEvent({ silent: true });
      setTaskEdits(prev => { const n = { ...prev }; delete n[volunteerId]; return n; });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la tâche:', error);
    } finally {
      setSavingTask(null);
    }
  };

  // ── Dispatch des modals de confirmation ─────────────────────────────────────
  const handleConfirmModal = async (sendNotif) => {
    const modal = confirmModal;
    setConfirmModal(null);
    if (!modal) return;

    if (modal.type === 'publish') {
      await handleStatusChange('publie', sendNotif);
    } else if (modal.type === 'deleteEvent') {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/events/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        navigate(backUrl);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    } else if (modal.type === 'deleteDoc') {
      await executeDeleteDocument(modal.payload.documentId);
    } else if (modal.type === 'volunteerDecision') {
      await executeVolunteerDecision(modal.payload.volunteerId, modal.payload.action, sendNotif);
    } else if (modal.type === 'removeVolunteer') {
      try {
        setRemovingVolunteer(modal.payload.volunteerId);
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/events/${id}/volunteers/${modal.payload.volunteerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTaskEdits((prev) => {
          const n = { ...prev };
          delete n[modal.payload.volunteerId];
          return n;
        });
        await fetchEvent({ silent: true });
        toast.success('Volontaire retiré de la liste');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Impossible de retirer le volontaire');
      } finally {
        setRemovingVolunteer(null);
      }
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const formatDate = (d) =>
    new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });


  const getStatusBadge = () => {
    const now = new Date();

    if (event.status === 'annule') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-red-100 text-red-800">
          Évènement annulé
        </span>
      );
    }

    if (event.status === 'brouillon') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
          Évènement non publié
        </span>
      );
    }

    // Pour les statuts publie / termine : se baser sur les dates réelles
    const sorted = [...(event.dates || [])].sort(
      (a, b) => new Date(a.startDate) - new Date(b.startDate)
    );

    if (sorted.length === 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Publié
        </span>
      );
    }

    const first = sorted[0];
    const last  = sorted[sorted.length - 1];

    const firstStart = new Date(first.startDate);

    // Fin du dernier créneau : endDate si dispo, sinon fin de journée du startDate
    const lastEnd = last.endDate
      ? new Date(last.endDate)
      : (() => { const d = new Date(last.startDate); d.setHours(23, 59, 59, 999); return d; })();

    if (now > lastEnd) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
          Évènement passé
        </span>
      );
    }

    if (now >= firstStart) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Évènement en cours
        </span>
      );
    }

    // À venir
    const dateLabel = new Date(first.startDate).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
    const timeLabel = first.startTime || null;

    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
        Commence le {dateLabel}{timeLabel ? ` à ${timeLabel}` : ''}
      </span>
    );
  };

  // ── États de chargement / erreur ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement de l'événement...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Événement non trouvé</h2>
          <button
            onClick={() => navigate(backUrl)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            {isAdminContext ? 'Retour aux événements' : 'Retour au dashboard'}
          </button>
        </div>
      </div>
    );
  }

  // ── Permissions ─────────────────────────────────────────────────────────────
  const canManage = userRole === 'admin' || userRole === 'pilot' || userRole === 'referent';
  // Les admins/pilotes peuvent accéder à la page d'édition complète (AdminRoute)
  const canUseFullEditPage = userRole === 'admin' || userRole === 'pilot';

  // ── Rendu ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between pt-8 gap-4">

          {/* Gauche : retour */}
          <div className="flex items-center min-w-0">
            <button
              onClick={() => navigate(backUrl)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors group"
            >
              <ArrowLeftIcon className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
              {isAdminContext ? 'Retour aux événements' : 'Retour aux événements'}
            </button>
          </div>

        </div>
      </div>

      {/* ── Corps ──────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">

        {/* ── Panneau de gestion (référents / pilotes / admins uniquement) ── */}
        {canManage && (
          <div className="rounded-xl border border-purple-200 shadow-sm overflow-hidden">

            {/* En-tête */}
            <div className="bg-purple-50 border-b border-purple-100 px-6 py-3 flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                <LockClosedIcon className="h-4 w-4" />
                Espace de gestion
              </span>
              <span className="text-sm text-purple-500">
                {userRole === 'admin'   ? 'Vous êtes administrateur(ice)' :
                 userRole === 'pilot'   ? 'Vous êtes pilote'              :
                                         'Vous êtes référent(e)'}
              </span>
            </div>

            {/* Barre d'onglets */}
            {(() => {
              const pendingCount = event.volunteers?.filter(v => v.status === 'pending').length || 0;
              return (
                <div className="border-b border-purple-100 bg-white px-6">
                  <nav className="-mb-px flex gap-0 flex-wrap">
                    {[
                      { key: 'volontaires',  label: 'Volontaires',           count: event.volunteers?.length || 0, show: true },
                      { key: 'modalites',    label: "Modalités d'organisation", count: null, show: true },
                      { key: 'documents',    label: 'Documents',             count: event.documents?.length  || 0, show: true },
                      { key: 'modifier',     label: "Modifier l'évènement",  count: null, show: true },
                      { key: 'informations', label: 'Informations',          count: null, show: true },
                      { key: 'partager',     label: 'Partager',              count: null, show: userRole === 'admin' || userRole === 'pilot' },
                    ].filter(t => t.show).map(({ key, label, count }) => (
                      <button
                        key={key}
                        onClick={() => {
                          setActiveManagementTab(key);
                          if (key === 'modifier') setEventForm({ title: event.title, description: event.description, status: event.status });
                        }}
                        className={`flex items-center gap-1.5 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                          activeManagementTab === key
                            ? 'border-purple-500 text-purple-700'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {label}
                        {/* Badge total */}
                        {count !== null && (
                          <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                            activeManagementTab === key
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>{count}</span>
                        )}
                        {/* Badge candidatures en attente (onglet volontaires uniquement) */}
                        {key === 'volontaires' && pendingCount > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900">
                            {pendingCount} en attente
                          </span>
                        )}
                      </button>
                    ))}
                  </nav>
                </div>
              );
            })()}

            {/* Contenu de l'onglet actif */}
            <div className="bg-white p-6">

              {/* ── Onglet Volontaires ── */}
              {activeManagementTab === 'volontaires' && (
                <div>
                  {/* Alerte candidatures en attente */}
                  {(() => {
                    const pending = event.volunteers?.filter(v => v.status === 'pending') || [];
                    if (!pending.length) return null;
                    return (
                      <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                        <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-yellow-400 text-yellow-900 text-sm font-bold">
                          {pending.length}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-yellow-900">
                            {pending.length === 1
                              ? '1 volontaire en attente de traitement'
                              : `${pending.length} volontaires en attente de traitement`}
                          </p>
                          <p className="text-xs text-yellow-700 mt-0.5">
                            {pending.map(v => `${v.userId?.prenom} ${v.userId?.nom}`).join(', ')}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Membre de l'équipe (référent / pilote / super admin) : inscription volontaire auto-validée + notification habituelle */}
                  {event.status === 'publie' &&
                    !event.volunteers?.some((v) => {
                      const uid = v.userId?._id ?? v.userId;
                      return uid && String(uid) === String(user._id);
                    }) && (
                      <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50/70 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-indigo-900">Me porter volontaire</p>
                          <p className="text-xs text-indigo-700 mt-0.5">
                            Votre nom est ajouté aux volontaires <strong>acceptés</strong>. Les référents et pilotes du module événements reçoivent la même notification que pour une candidature adhérent.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleVolunteerAction('register')}
                          disabled={volunteerLoading}
                          className="inline-flex items-center justify-center shrink-0 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                        >
                          <HandRaisedIcon className="h-5 w-5 mr-2" />
                          {volunteerLoading ? '…' : "M'inscrire"}
                        </button>
                      </div>
                    )}

                  {event.volunteers?.length > 0 ? (
                    <div className="space-y-3">
                      {event.volunteers.map((volunteer) => {
                        const isEditing = !!taskEdits[volunteer._id];
                        const edit = taskEdits[volunteer._id] || {};
                        return (
                          <div key={volunteer._id} className="bg-purple-50/40 border border-purple-100 rounded-xl p-3">
                            {/* Ligne principale : identité + statut + actions */}
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">

                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2 min-w-0">
                                    <p className="font-medium text-gray-900 text-md truncate">
                                      {volunteer.userId?.prenom} {volunteer.userId?.nom}
                                    </p>
                                    {(volunteer.adhesionOrganismesActifs || []).length > 0 && (
                                      <span className="flex flex-wrap gap-1 shrink-0">
                                        {(volunteer.adhesionOrganismesActifs || []).map((org) => (
                                          <span
                                            key={org}
                                            className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-100"
                                            title="Adhésion active pour l'année en cours"
                                          >
                                            {org}
                                          </span>
                                        ))}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap gap-x-3 text-sm text-gray-500 mt-0.5">
                                    {volunteer.userId?.email && (
                                      <span className="flex items-center gap-1">
                                        <EnvelopeIcon className="h-3 w-3" />{volunteer.userId.email}
                                      </span>
                                    )}
                                    {(volunteer.userId?.telephone || volunteer.userId?.telephoneMobile) && (
                                      <span className="flex items-center gap-1">
                                        <PhoneIcon className="h-3 w-3" />
                                        {volunteer.userId.telephoneMobile || volunteer.userId.telephone}
                                      </span>
                                    )}
                                  </div>
                                  {volunteer.message && (
                                    <p className="text-xs text-gray-500 mt-1 italic">"{volunteer.message}"</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  volunteer.status === 'pending'  ? 'bg-yellow-100 text-yellow-800' :
                                  volunteer.status === 'accepted' ? 'bg-green-100 text-green-800'  :
                                  volunteer.status === 'rejected' ? 'bg-red-100 text-red-800'      :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {volunteer.status === 'pending'  ? 'En attente' :
                                   volunteer.status === 'accepted' ? 'Accepté'    :
                                   volunteer.status === 'rejected' ? 'Refusé'     : volunteer.status}
                                </span>
                                {volunteer.status === 'pending' && (
                                  <div className="flex gap-1">
                                    <button
                                      onClick={() => handleVolunteerDecision(volunteer._id, 'accepte')}
                                      disabled={processingVolunteer === volunteer._id}
                                      title="Accepter"
                                      className="p-1.5 rounded-md text-green-600 hover:bg-green-50 disabled:opacity-50"
                                    >
                                      <CheckIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleVolunteerDecision(volunteer._id, 'refuse')}
                                      disabled={processingVolunteer === volunteer._id}
                                      title="Refuser"
                                      className="p-1.5 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                                {/* Bouton d'édition de tâche (volontaires acceptés) */}
                                {volunteer.status === 'accepted' && !isEditing && (
                                  <button
                                    onClick={() => initTaskEdit(volunteer)}
                                    title="Assigner une tâche"
                                    className="p-1.5 rounded-md text-indigo-500 hover:bg-indigo-50"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveVolunteerRequest(volunteer)}
                                  disabled={
                                    processingVolunteer === volunteer._id ||
                                    removingVolunteer === volunteer._id
                                  }
                                  title="Retirer de la liste"
                                  className="p-1.5 rounded-md text-red-500 hover:bg-red-50 disabled:opacity-50"
                                >
                                  {removingVolunteer === volunteer._id ? (
                                    <div className="h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <TrashIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </div>

                            {/* Tâche existante (affichage) */}
                            {!isEditing && volunteer.task && (
                              <div className="mt-2 p-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Tâche</p>
                                <p className="text-sm text-indigo-900">{volunteer.task}</p>
                                {volunteer.taskNote && (
                                  <p className="text-xs text-indigo-600 mt-1 italic">{volunteer.taskNote}</p>
                                )}
                              </div>
                            )}

                            {/* Formulaire d'édition de tâche */}
                            {isEditing && (
                              <div className="mt-2 space-y-2">
                                <p className="text-sm text-indigo-600 flex items-center gap-1">
                                  <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                  Ces informations seront visibles par le volontaire dans son espace personnel.
                                </p>
                                <input
                                  type="text"
                                  placeholder="Tâche assignée (ex : accueil, logistique…)"
                                  value={edit.task}
                                  onChange={e => setTaskEdits(prev => ({ ...prev, [volunteer._id]: { ...prev[volunteer._id], task: e.target.value } }))}
                                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                />
                                <textarea
                                  rows={2}
                                  placeholder="Note complémentaire (optionnel)"
                                  value={edit.taskNote}
                                  onChange={e => setTaskEdits(prev => ({ ...prev, [volunteer._id]: { ...prev[volunteer._id], taskNote: e.target.value } }))}
                                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
                                />
                                <div className="flex gap-2 justify-end">
                                  <button
                                    onClick={() => setTaskEdits(prev => { const n = { ...prev }; delete n[volunteer._id]; return n; })}
                                    className="px-3 py-1 text-xs border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                                  >
                                    Annuler
                                  </button>
                                  <button
                                    onClick={() => saveVolunteerTask(volunteer._id)}
                                    disabled={savingTask === volunteer._id}
                                    className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
                                  >
                                    {savingTask === volunteer._id
                                      ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                      : <CheckIcon className="h-3 w-3" />}
                                    Enregistrer
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-6">Pas de volontaires pour le moment.</p>
                  )}
                </div>
              )}

              {/* ── Onglet Modalités d'organisation (volontaires acceptés) ── */}
              {activeManagementTab === 'modalites' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Ce texte est affiché aux <strong>volontaires dont la candidature est acceptée</strong>, dans l&apos;onglet dédié de leur encart.
                  </p>
                  {event.modalitesOrganisation &&
                  event.modalitesOrganisation.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim() ? (
                    <div
                      className="mb-4 rounded-lg border border-purple-100 bg-gray-50/80 p-4 prose prose-sm max-w-none text-gray-800"
                      dangerouslySetInnerHTML={{ __html: event.modalitesOrganisation }}
                    />
                  ) : (
                    <p className="text-sm text-gray-500 italic mb-4">Aucun contenu pour le moment. Cliquez sur « Modifier » pour rédiger les modalités.</p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setModalitesDraft(event.modalitesOrganisation || '');
                      setModalitesModalOpen(true);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Modifier
                  </button>
                </div>
              )}

              {/* ── Onglet Documents ── */}
              {activeManagementTab === 'documents' && (() => {
                const audienceCfg = {
                  all:                  { label: 'Tous les participants', cls: 'bg-green-100 text-green-700'   },
                  team_with_volunteers: { label: 'Équipe + volontaires',  cls: 'bg-blue-100 text-blue-700'    },
                  team_only:            { label: 'Équipe uniquement',     cls: 'bg-purple-100 text-purple-700' },
                };
                const docs = event.documents || [];
                return (
                  <div>
                    <div className="flex justify-start mb-4">
                      <button
                        onClick={() => setShowDocumentModal(true)}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                        Ajouter un document
                      </button>
                    </div>
                    {docs.length > 0 ? (
                      <div className="divide-y divide-purple-100">
                        {docs.map((doc) => {
                          const audience      = doc.audience || 'all';
                          const cfg           = audienceCfg[audience] || audienceCfg.all;
                          const isUpdating    = updatingDocAudience === doc._id;
                          const isEditingNote = docNoteEdits[doc._id] !== undefined;
                          const isSavingNote  = savingDocNote === doc._id;
                          return (
                            <div key={doc._id} className="py-3 first:pt-0 last:pb-0">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2 min-w-0 flex-1">
                                  <DocumentTextIcon className="h-4 w-4 text-purple-400 flex-shrink-0 mt-0.5" />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {doc.originalName || doc.fileName}
                                    </p>

                                    {/* Note — affichage ou édition inline */}
                                    {isEditingNote ? (
                                      <div className="mt-1.5 flex items-start gap-1.5">
                                        <textarea
                                          rows={2}
                                          value={docNoteEdits[doc._id]}
                                          onChange={e => setDocNoteEdits(prev => ({ ...prev, [doc._id]: e.target.value.slice(0, 140) }))}
                                          maxLength={140}
                                          className="flex-1 text-xs border border-purple-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-400 resize-none"
                                          placeholder="Note associée au document…"
                                        />
                                        <p className="text-right text-xs text-gray-400 mt-0.5">
                                          {(docNoteEdits[doc._id] || '').length}/140
                                        </p>
                                        <div className="flex flex-col gap-1">
                                          <button
                                            onClick={() => saveDocumentNote(doc._id)}
                                            disabled={isSavingNote}
                                            className="p-1 text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50"
                                            title="Enregistrer"
                                          >
                                            <CheckIcon className="h-3.5 w-3.5" />
                                          </button>
                                          <button
                                            onClick={() => setDocNoteEdits(prev => { const n = { ...prev }; delete n[doc._id]; return n; })}
                                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                                            title="Annuler"
                                          >
                                            <XMarkIcon className="h-3.5 w-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-1 mt-0.5">
                                        {doc.note
                                          ? <p className="text-xs text-gray-500">{doc.note}</p>
                                          : <p className="text-xs text-gray-400 italic">Aucune note</p>
                                        }
                                        <button
                                          onClick={() => setDocNoteEdits(prev => ({ ...prev, [doc._id]: doc.note || '' }))}
                                          className="p-0.5 text-gray-400 hover:text-purple-500 flex-shrink-0"
                                          title="Modifier la note"
                                        >
                                          <PencilIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    )}

                                    <span className={`inline-block mt-1 text-xs px-1.5 py-0.5 rounded font-medium ${cfg.cls}`}>
                                      {cfg.label}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <select
                                    value={audience}
                                    onChange={(e) => handleDocumentAudienceChange(doc._id, e.target.value)}
                                    disabled={isUpdating}
                                    className="text-xs border border-gray-200 rounded px-1.5 py-1 text-gray-600 bg-white hover:border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-400 disabled:opacity-50"
                                  >
                                    <option value="all">Tous</option>
                                    <option value="team_only">Équipe seule</option>
                                    <option value="team_with_volunteers">Équipe + volontaires</option>
                                  </select>
                                  <button
                                    onClick={async () => {
                                      const token = localStorage.getItem('token');
                                      const response = await axios.get(
                                        `${process.env.REACT_APP_API_URL}/events/${id}/documents/${doc._id}/download`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      window.open(response.data.downloadUrl, '_blank');
                                    }}
                                    title="Voir le document"
                                    className="p-1 text-gray-400 hover:text-indigo-600 transition-colors rounded"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteDocument(doc._id)}
                                    title="Supprimer"
                                    className="p-1 text-gray-400 hover:text-red-600 transition-colors rounded"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-6">Aucun document pour le moment.</p>
                    )}
                  </div>
                );
              })()}

              {/* ── Onglet Modifier ── */}
              {activeManagementTab === 'modifier' && (
                <div className="flex flex-col items-center gap-4 py-6 text-center">
                  <p className="text-sm text-gray-500">
                    Accédez à la page de modification pour éditer toutes les informations de l'événement.
                  </p>
                  <button
                    onClick={() => navigate(canUseFullEditPage ? `/admin/events/${id}/edit` : `/events/${id}/edit`)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Ouvrir la page de modification
                  </button>
                </div>
              )}

              {/* ── Onglet Informations ── */}
              {/* ── Onglet Partager ── */}
              {activeManagementTab === 'partager' && (() => {
                const comm = event.communicationEvenement;
                return (
                  <div className="space-y-5">
                    {/* Explication */}
                    <div className="flex items-start gap-3 p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
                      <MegaphoneIcon className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-indigo-800 space-y-1">
                        <p className="font-semibold">Partager l'événement avec les adhérents</p>
                        <p>
                          Cette action crée un <strong>brouillon de communication</strong> pré-rempli avec le titre, la description et les dates de l'événement.
                          Les destinataires sont pré-sélectionnés : les adhérents actifs de l&apos;année en cours pour chaque organisme concerné
                          ({(event.organismesConcernes?.length ? event.organismesConcernes : event.organismes)?.join(', ') || '—'}).
                        </p>
                        <p>
                          Vous serez redirigé(e) vers le formulaire d'édition pour vérifier, compléter et envoyer (ou programmer) la communication.
                        </p>
                      </div>
                    </div>

                    {/* État : jamais créée */}
                    {!comm && (
                      <button
                        onClick={handleCreateCommunication}
                        disabled={creatingCommunication}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      >
                        <MegaphoneIcon className="h-4 w-4" />
                        {creatingCommunication ? 'Création en cours…' : 'Créer la communication'}
                      </button>
                    )}

                    {/* État : brouillon */}
                    {comm?.statut === 'brouillon' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                          <span className="font-semibold">⚠️ Un brouillon existe</span>
                          — la communication n'a pas encore été envoyée.
                        </div>
                        <button
                          onClick={() => navigate(`/admin/communications/edit/${comm._id}`)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-yellow-500 text-white text-sm font-semibold rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                          Modifier / envoyer le brouillon
                        </button>
                        <p className="text-xs text-gray-400">
                          Si vous supprimez ce brouillon depuis le module Communications, vous pourrez créer une nouvelle communication à partir de ce bouton.
                        </p>
                      </div>
                    )}

                    {/* État : programmée */}
                    {comm?.statut === 'programme' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                          <span className="font-semibold">🕐 Communication programmée</span>
                        </div>
                        <button
                          onClick={() => navigate(`/admin/communications/edit/${comm._id}`)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Voir la communication programmée
                        </button>
                      </div>
                    )}

                    {/* État : envoyée */}
                    {comm?.statut === 'envoye' && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                          <span className="font-semibold">✅ Communication envoyée</span>
                          {comm.dateEnvoi && (
                            <span className="text-green-600">
                              — le {new Date(comm.dateEnvoi).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => navigate(`/admin/communications/${comm._id}`)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Voir le détail de la communication
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {activeManagementTab === 'informations' && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Statut</span>
                    {getStatusBadge()}
                  </div>
                  <div className="flex items-start justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Organismes concernés</span>
                    <div className="flex flex-wrap gap-1 justify-end max-w-xs">
                      {(event.organismesConcernes?.length ? event.organismesConcernes : event.organismes)?.length > 0
                        ? (event.organismesConcernes?.length ? event.organismesConcernes : event.organismes).map((org, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{org}</span>
                          ))
                        : <span className="text-sm text-gray-400">—</span>}
                    </div>
                  </div>
                  <div className="flex items-start justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Organismes organisateurs</span>
                    <div className="flex flex-wrap gap-1 justify-end max-w-xs">
                      {(event.organismesOrganisateurs?.length
                        ? event.organismesOrganisateurs
                        : (event.organismesConcernes?.length ? event.organismesConcernes : event.organismes)
                      )?.length > 0
                        ? (event.organismesOrganisateurs?.length
                            ? event.organismesOrganisateurs
                            : (event.organismesConcernes?.length ? event.organismesConcernes : event.organismes)
                          ).map((org, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-indigo-50 text-indigo-800 rounded border border-indigo-100">{org}</span>
                          ))
                        : <span className="text-sm text-gray-400">—</span>}
                    </div>
                  </div>
                  <div className="flex items-start justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Référent(s)</span>
                    <div className="text-right">
                      {event.referents?.length > 0
                        ? event.referents.map((ref, i) => (
                            <p key={i} className="text-sm text-gray-700">{ref.prenom} {ref.nom}</p>
                          ))
                        : <span className="text-sm text-gray-400">—</span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Volontaires inscrits</span>
                    <span className="text-sm font-semibold text-gray-900">{event.volunteers?.length || 0}</span>
                  </div>
                  {event.createdBy && (
                    <div className="flex items-center justify-between py-3">
                      <span className="text-sm font-medium text-gray-500">Créé par</span>
                      <span className="text-sm text-gray-700">{event.createdBy.prenom} {event.createdBy.nom}</span>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        )}

        {/* ── Panneau volontaire (onglets participation / documents) ── */}
        {userRole === 'volunteer' && (() => {
          const encartDocCount = event.documents?.length || 0;
          return (
            <div className="rounded-xl border border-blue-200 shadow-sm overflow-hidden">

              <div className="bg-blue-50 border-b border-blue-100 px-6 py-3 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                  <HandRaisedIcon className="h-4 w-4" />
                  Volontariat
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  event.volunteerStatus?.status === 'pending'  ? 'bg-yellow-100 text-yellow-800' :
                  event.volunteerStatus?.status === 'accepted' ? 'bg-green-100  text-green-800'  :
                  event.volunteerStatus?.status === 'rejected' ? 'bg-red-100    text-red-800'    :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {event.volunteerStatus?.status === 'pending'  ? 'Candidature en attente' :
                   event.volunteerStatus?.status === 'accepted' ? 'Participation confirmée' :
                   event.volunteerStatus?.status === 'rejected' ? 'Candidature non retenue' : ''}
                </span>
              </div>

              <div className="border-b border-blue-100 bg-white px-6">
                <nav className="-mb-px flex gap-0 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setVolunteerEncartTab('participation')}
                    className={`flex items-center gap-1.5 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                      volunteerEncartTab === 'participation'
                        ? 'border-blue-500 text-blue-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Votre participation
                  </button>
                  {event.volunteerStatus?.status === 'accepted' && (
                    <button
                      type="button"
                      onClick={() => setVolunteerEncartTab('modalites')}
                      className={`flex items-center gap-1.5 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                        volunteerEncartTab === 'modalites'
                          ? 'border-blue-500 text-blue-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Modalités d&apos;organisation
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setVolunteerEncartTab('documents')}
                    className={`flex items-center gap-1.5 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                      volunteerEncartTab === 'documents'
                        ? 'border-blue-500 text-blue-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Documents
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      volunteerEncartTab === 'documents' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                    }`}>{encartDocCount}</span>
                  </button>
                </nav>
              </div>

              <div className="bg-white p-6">
                {volunteerEncartTab === 'participation' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                        {event.referents?.length > 1 ? 'Référents' : 'Référent'}
                      </p>
                      {event.referents?.length > 0 ? (
                        <div className="space-y-3">
                          {event.referents.map((ref) => (
                            <div key={ref._id} className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <UserCircleIcon className="h-5 w-5 text-blue-400" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {ref.prenom} {ref.nom}
                                </p>
                                <div className="mt-0.5 space-y-0.5">
                                  {ref.email && (
                                    <a
                                      href={`mailto:${ref.email}`}
                                      className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                    >
                                      <EnvelopeIcon className="h-3 w-3 flex-shrink-0" />
                                      {ref.email}
                                    </a>
                                  )}
                                  {(ref.telephoneMobile || ref.telephone) && (
                                    <a
                                      href={`tel:${ref.telephoneMobile || ref.telephone}`}
                                      className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                    >
                                      <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                                      {ref.telephoneMobile || ref.telephone}
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">Aucun référent désigné</p>
                      )}
                    </div>

                    <div>
                      {event.volunteerStatus?.status === 'accepted' && (
                        <>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Votre tâche</p>
                          {event.volunteerStatus.task ? (
                            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg space-y-1">
                              <p className="text-sm font-medium text-indigo-900">{event.volunteerStatus.task}</p>
                              {event.volunteerStatus.taskNote && (
                                <p className="text-xs text-indigo-600">{event.volunteerStatus.taskNote}</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 italic">Aucune tâche assignée pour le moment</p>
                          )}
                        </>
                      )}

                      {event.volunteerStatus?.status === 'pending' && (
                        <>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Ma candidature</p>
                          <p className="text-sm text-gray-500 mb-4">
                            Votre candidature est en cours de traitement par l'équipe organisatrice.
                          </p>
                          <button
                            type="button"
                            onClick={() => handleVolunteerAction('unregister')}
                            disabled={volunteerLoading}
                            className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-200 text-sm font-medium rounded-lg text-red-600 bg-white hover:bg-red-50 disabled:opacity-50"
                          >
                            {volunteerLoading
                              ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                              : <XMarkIcon className="h-4 w-4" />}
                            Annuler ma candidature
                          </button>
                        </>
                      )}

                      {event.volunteerStatus?.status === 'rejected' && (
                        <p className="text-sm text-gray-500 italic mt-6">
                          Nous vous remercions pour votre intérêt. N'hésitez pas à vous manifester pour de futurs événements.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {event.volunteerStatus?.status === 'accepted' && volunteerEncartTab === 'modalites' && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Modalités d&apos;organisation</h4>
                    {event.modalitesOrganisation &&
                    event.modalitesOrganisation.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim() ? (
                      <div
                        className="prose prose-sm max-w-none text-gray-800 [&_a]:text-blue-600"
                        dangerouslySetInnerHTML={{ __html: event.modalitesOrganisation }}
                      />
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        Aucune modalité d&apos;organisation n&apos;a encore été publiée par l&apos;équipe.
                      </p>
                    )}
                  </div>
                )}

                {volunteerEncartTab === 'documents' && (
                  <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 overflow-hidden">
                    {(event.documents || []).length === 0 ? (
                      <p className="p-4 text-sm text-gray-400 italic">Aucun document disponible pour le moment.</p>
                    ) : (
                      (event.documents || []).map((doc) => {
                        const aud = getDocAudienceKey(doc);
                        return (
                          <div key={doc._id} className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50/50">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {doc.originalName || doc.fileName}
                                </p>
                                {aud === 'all' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200">
                                    Document public
                                  </span>
                                )}
                                {aud === 'team_with_volunteers' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
                                    Organisation
                                  </span>
                                )}
                                {aud === 'team_only' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                    Équipe
                                  </span>
                                )}
                              </div>
                              {doc.note && <p className="text-xs text-gray-500 mt-1">{doc.note}</p>}
                            </div>
                            <button
                              type="button"
                              onClick={() => downloadEventDocument(doc._id)}
                              title="Voir le document"
                              className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded flex-shrink-0"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Encart volontariat (non inscrit) : onglets participation / documents ── */}
        {userRole === 'public' && (() => {
          const encartDocCount = event.documents?.length || 0;
          return (
            <div className="rounded-xl border border-indigo-200 shadow-sm overflow-hidden">
              <div className="bg-indigo-50 border-b border-indigo-100 px-6 py-3 flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
                  <HandRaisedIcon className="h-4 w-4" />
                  Volontariat
                </span>
              </div>

              <div className="border-b border-indigo-100 bg-white px-6">
                <nav className="-mb-px flex gap-0 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setVolunteerEncartTab('participation')}
                    className={`flex items-center gap-1.5 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                      volunteerEncartTab === 'participation'
                        ? 'border-indigo-500 text-indigo-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Participation
                  </button>
                  <button
                    type="button"
                    onClick={() => setVolunteerEncartTab('documents')}
                    className={`flex items-center gap-1.5 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                      volunteerEncartTab === 'documents'
                        ? 'border-indigo-500 text-indigo-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Documents
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      volunteerEncartTab === 'documents' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-500'
                    }`}>{encartDocCount}</span>
                  </button>
                </nav>
              </div>

              <div className="bg-white p-6">
                {volunteerEncartTab === 'participation' && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <HandRaisedIcon className="h-5 w-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-indigo-700">
                        Nous cherchons des volontaires pour cet événement — rejoignez l'équipe organisatrice&nbsp;!
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleVolunteerAction('register')}
                      disabled={volunteerLoading}
                      className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {volunteerLoading
                        ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        : <HandRaisedIcon className="h-4 w-4" />}
                      Je suis volontaire
                    </button>
                  </div>
                )}

                {volunteerEncartTab === 'documents' && (
                  <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 overflow-hidden">
                    {(event.documents || []).length === 0 ? (
                      <p className="p-4 text-sm text-gray-400 italic">Aucun document disponible pour le moment.</p>
                    ) : (
                      (event.documents || []).map((doc) => {
                        const aud = getDocAudienceKey(doc);
                        return (
                          <div key={doc._id} className="flex items-start justify-between gap-3 px-4 py-3 bg-gray-50/50">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {doc.originalName || doc.fileName}
                                </p>
                                {aud === 'all' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200">
                                    Document public
                                  </span>
                                )}
                                {aud === 'team_with_volunteers' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
                                    Organisation
                                  </span>
                                )}
                                {aud === 'team_only' && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                    Équipe
                                  </span>
                                )}
                              </div>
                              {doc.note && <p className="text-xs text-gray-500 mt-1">{doc.note}</p>}
                            </div>
                            <button
                              type="button"
                              onClick={() => downloadEventDocument(doc._id)}
                              title="Voir le document"
                              className="p-1.5 text-gray-400 hover:text-indigo-600 transition-colors rounded flex-shrink-0"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Grille principale : contenu public ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          {/* ── Colonne gauche : image + infos ── */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-xl overflow-hidden">

              {/* Image de couverture — sans padding, dimensions naturelles */}
              {event.coverImage?.url && (
                <img
                  src={event.coverImage.url}
                  alt={event.title}
                  className="w-full h-auto block"
                />
              )}

              {/* Badge statut + titre + description */}
              <div className="p-6">
                {getStatusBadge()}
                <h2 className="text-2xl font-bold text-gray-900 mt-3 mb-3 leading-snug">
                  {event.title}
                </h2>
                <div
                  className="text-sm text-gray-600 prose prose-sm max-w-none leading-relaxed [&_a]:text-indigo-600"
                  dangerouslySetInnerHTML={{ __html: event.description || '' }}
                />


                {userRole === 'volunteer' && (
                  <div className="mt-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                      event.volunteerStatus?.status === 'pending'  ? 'bg-yellow-100 text-yellow-800' :
                      event.volunteerStatus?.status === 'accepted' ? 'bg-green-100  text-green-800'  :
                      event.volunteerStatus?.status === 'rejected' ? 'bg-red-100    text-red-800'    :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {event.volunteerStatus?.status === 'pending'  && <><CheckIcon className="h-4 w-4" /> Candidature en attente</>}
                      {event.volunteerStatus?.status === 'accepted' && <><CheckIcon className="h-4 w-4" /> Participation confirmée</>}
                      {event.volunteerStatus?.status === 'rejected' && <><XMarkIcon className="h-4 w-4" /> Candidature non retenue</>}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Colonne droite : dates + documents publics ── */}
          <div className="lg:col-span-1 space-y-8">

            {/* Dates et lieux */}
            {event.dates?.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                  <CalendarDaysIcon className="h-5 w-5 mr-2 text-indigo-600" />
                  Dates et lieux
                </h2>
                <div className="grid grid-cols-1 gap-4">
                  {event.dates.map((date, index) => (
                    <div key={index} className="bg-white shadow rounded-r-xl border-l-4 border-indigo-600 p-5 flex flex-col gap-2">
                      {date.description && (
                        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest">
                          {date.description}
                        </p>
                      )}
                      <p className="font-semibold text-gray-900 capitalize">
                        {date.endDate && date.endDate.slice(0, 10) !== date.startDate.slice(0, 10)
                          ? <>Du {formatDate(date.startDate)}<br/>au {formatDate(date.endDate)}</>
                          : formatDate(date.startDate)}
                      </p>
                      <div className="flex items-center gap-3">
                        {(date.startTime || date.endTime) && (
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <ClockIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            {date.startTime || ''}
                            {date.endTime && ` – ${date.endTime}`}
                          </p>
                        )}
                        {date.location && (
                          <p className="text-sm text-gray-600 flex items-start gap-1">
                            <MapPinIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" />
                            <span>
                              {typeof date.location === 'string'
                                ? date.location
                                : date.location.name
                                ? date.location.name
                                : [date.location.address, date.location.city, date.location.postalCode]
                                    .filter(Boolean).join(', ')}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents (colonne droite) — masqués si déjà dans l'encart volontariat (onglet Documents) */}
            {!(userRole === 'volunteer' || userRole === 'public') && (() => {
              const publicDocs = event.documents || [];
              return (
                <div>
                  <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-indigo-600" />
                    Documents
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                      {publicDocs.length}
                    </span>
                  </h2>
                  <div className="bg-white shadow rounded-xl p-6">
                    <div className="divide-y divide-gray-100">
                      {publicDocs.length === 0 ? <p className="text-sm text-gray-400 italic">Aucun document disponible pour le moment.</p> : (
                      publicDocs.map((doc) => (
                        <div key={doc._id} className="flex items-center justify-between py-3 gap-3 first:pt-0 last:pb-0">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {doc.originalName || doc.fileName}
                              </p>
                              {doc.note && (
                                <p className="text-xs text-gray-500 mt-0.5">{doc.note}</p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => downloadEventDocument(doc._id)}
                            title="Voir le document"
                            className="p-1.5 text-gray-400 hover:text-indigo-600 transition-colors rounded flex-shrink-0"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

      </div>


      {/* ── Modal d'upload de documents ────────────────────────────────────── */}
      {showDocumentModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-xl bg-white">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Ajouter un document</h3>
            <form onSubmit={handleDocumentUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fichier</label>
                <FileDropzone
                  onFileSelect={(file) => setDocumentForm({ ...documentForm, file })}
                  acceptedTypes=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (optionnelle)</label>
                <textarea
                  value={documentForm.note}
                  onChange={e => setDocumentForm({ ...documentForm, note: e.target.value.slice(0, 140) })}
                  maxLength={140}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Description du document..."
                />
                <p className="text-right text-xs text-gray-400 mt-0.5">{documentForm.note.length}/140</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Niveau d'accès</label>
                <select
                  value={documentForm.accessLevel}
                  onChange={e => setDocumentForm({ ...documentForm, accessLevel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Tous (adhérents, volontaires, référents, pilotes)</option>
                  <option value="team_with_volunteers">Pilotes, référents et volontaires</option>
                  <option value="team_only">Pilotes et référents</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={uploadingDocument}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {uploadingDocument ? 'Upload…' : 'Ajouter'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDocumentModal(false); setDocumentForm({ file: null, note: '', accessLevel: 'all' }); }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal édition inline (référents sans accès admin) ──────────────── */}
      {editingEvent && canManage && !canUseFullEditPage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Modifier l'événement</h3>
            <form onSubmit={handleSaveEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={eventForm.title}
                  onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={eventForm.description}
                  onChange={e => setEventForm({ ...eventForm, description: e.target.value })}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={eventForm.status}
                  onChange={e => setEventForm({ ...eventForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="brouillon">Brouillon</option>
                  <option value="publie">Publié</option>
                  <option value="annule">Annulé</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Enregistrer
                </button>
                <button
                  type="button"
                  onClick={() => setEditingEvent(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalitesModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => !savingModalites && setModalitesModalOpen(false)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modalites-organisation-title"
          >
            <h3 id="modalites-organisation-title" className="text-lg font-semibold text-gray-900 mb-1">
              Modalités d&apos;organisation
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Ce texte est visible par les volontaires dont la candidature est acceptée.
            </p>
            <Editor
              apiKey="yz5jdxbkp4i9v4omefqdsmbc6pxaqmyor9p6sh5cfi2f3gz5"
              value={modalitesDraft}
              onEditorChange={(content) => setModalitesDraft(content)}
              init={{
                height: 360,
                menubar: false,
                plugins: ['lists', 'link', 'code'],
                toolbar:
                  'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link | code',
                content_style:
                  'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
                language: 'fr_FR',
              }}
            />
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setModalitesModalOpen(false)}
                disabled={savingModalites}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={saveModalitesOrganisation}
                disabled={savingModalites}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingModalites ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modals de confirmation ──────────────────────────────────────────── */}
      {confirmModal?.type === 'publish' && (
        <ConfirmModal
          title="Publier l'événement"
          message="Voulez-vous publier cet événement ? Les adhérents concernés pourront le consulter."
          confirmLabel="Publier"
          confirmClass="bg-green-600 hover:bg-green-700"
          onConfirm={handleConfirmModal}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal?.type === 'deleteEvent' && (
        <ConfirmModal
          title="Supprimer l'événement"
          message={`Êtes-vous sûr de vouloir supprimer l'événement "${event?.title}" ? Cette action est irréversible.`}
          confirmLabel="Supprimer définitivement"
          confirmClass="bg-red-600 hover:bg-red-700"
          onConfirm={handleConfirmModal}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal?.type === 'deleteDoc' && (
        <ConfirmModal
          title="Supprimer le document"
          message="Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible."
          confirmLabel="Supprimer"
          confirmClass="bg-red-600 hover:bg-red-700"
          onConfirm={handleConfirmModal}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal?.type === 'volunteerDecision' && (
        <ConfirmModal
          title={confirmModal.payload.action === 'accepte' ? 'Accepter le volontaire' : 'Refuser le volontaire'}
          message={confirmModal.payload.action === 'accepte'
            ? 'Voulez-vous accepter cette candidature de volontariat ?'
            : 'Voulez-vous refuser cette candidature de volontariat ?'}
          confirmLabel={confirmModal.payload.action === 'accepte' ? 'Accepter' : 'Refuser'}
          confirmClass={confirmModal.payload.action === 'accepte' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          withNotification
          notificationLabel="Notifier le volontaire par email"
          onConfirm={handleConfirmModal}
          onCancel={() => setConfirmModal(null)}
        />
      )}
      {confirmModal?.type === 'removeVolunteer' && (
        <ConfirmModal
          title="Retirer le volontaire"
          message={`Êtes-vous sûr de vouloir retirer ${confirmModal.payload.name} de la liste des volontaires ? La personne n’apparaîtra plus ici (elle pourra se porter volontaire à nouveau si l’événement y est toujours ouvert).`}
          confirmLabel="Retirer de la liste"
          confirmClass="bg-red-600 hover:bg-red-700"
          onConfirm={handleConfirmModal}
          onCancel={() => setConfirmModal(null)}
        />
      )}

    </div>
  );
}
